<template>
  <div class="flex h-full flex-col bg-white">
    <div ref="messageList" class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
      <template v-for="(msg, i) in messages" :key="i">
        <div
          class="text-body text-beige-90 text-pretty wrap-break-word"
          :class="[
            msg.role === 'user'
              ? 'max-w-4/5 self-end rounded-lg bg-gray-100 px-3 py-2'
              : 'flex items-start gap-2 self-start',
          ]"
        >
          <!-- v-html safe: msg.content here is always the INTRO_HTML static constant so never use isIntro with dynamic/server content -->
          <div v-if="msg.isIntro" v-html="msg.content" />
          <div v-else-if="msg.role === 'assistant'" class="flex flex-col gap-1">
            {{ msg.content }}
            <RfButtonNew
              v-if="i === lastUndoableIndex"
              type="table"
              size="none"
              class="self-start p-0.5!"
              :disabled="msg.undone || loading"
              :aria-disabled="msg.undone || loading || undefined"
              :aria-label="`Undo message ${i + 1}`"
              @click="undoMessage(msg)"
            >
              <RfUndoIcon class="h-5 w-5" />
            </RfButtonNew>
          </div>
          <template v-else>{{ msg.content }}</template>
        </div>
      </template>
      <div v-if="loading" class="text-beige-50 text-body self-start">Thinking…</div>
      <div
        v-if="error"
        class="text-vermillion-50 bg-vermillion-1 text-body self-start rounded-lg px-3 py-2"
      >
        {{ error }}
      </div>
    </div>

    <div class="flex flex-col gap-2 p-3">
      <RfTextarea
        v-model="input"
        placeholder="Describe a style change…"
        variant="outlined"
        rows="1"
        autoGrow
        :style="{ maxHeight: '8rem', overflowY: 'auto' }"
        counter="2000"
        maxlength="2000"
        :disabled="loading"
        @keydown.enter.exact.prevent="send"
      />
      <div class="flex items-center justify-between">
        <RfButtonNew
          type="table"
          size="small"
          class="text-beige-50 [&.disabled]:opacity-40"
          :loading="loading"
          :disabled="loading || !hasAiChanges"
          @click="revertDialogRef.open()"
        >
          <RfResetIcon class="fill-beige-50 h-5 w-5" />
          Revert All
        </RfButtonNew>

        <RfButtonNew
          type="main"
          size="small"
          class="[&_.rf-button--container]:flex-row-reverse"
          :loading="loading"
          :disabled="!input.trim()"
          @click="send"
        >
          <RfSendArrowIcon class="h-5 w-5" />
          Send
        </RfButtonNew>
      </div>
    </div>

    <RfDialog ref="revertDialogRef" title="Revert all changes from this session?">
      This will undo all styling changes made during this chat session and restore your prompt to
      how it looked before you started. This action cannot be undone.
      <template #footer>
        <RfButtonNew
          type="tertiary"
          class="!text-beige-50 border-beige-30"
          @click="revertDialogRef.close()"
        >
          <span class="px-4">Keep Changes</span>
        </RfButtonNew>
        <RfButtonNew
          type="main"
          class="!bg-vermillion-50 text-white hover:!brightness-90"
          @click="
            () => {
              revertAll();
              revertDialogRef.close();
            }
          "
        >
          <span class="px-4">Revert</span>
        </RfButtonNew>
      </template>
    </RfDialog>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";
import { useRoute } from "vue-router";
import ApiPaths from "@/apis/ApiPaths";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfTextarea from "@/components/inputs/RfTextarea.vue";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import RfSendArrowIcon from "@/components/icons/RfSendArrowIcon.vue";
import RfUndoIcon from "@/components/icons/RfUndoIcon.vue";
import RfDialog from "@/components/modals/RfDialog.vue";

const AI_SECTION_MARKER = "/* AI Style Studio — add manual CSS above this line */";
// static only so never interpolate user or server data here
const INTRO_HTML = `<p class="mb-2">Describe a style change in plain text (e.g., "Make the primary button larger" or "Change header color to #cc0000").</p><ul class="mb-2 list-disc pl-5"><li><strong>Devices</strong>: Edits apply to both desktop and mobile by default. Append "on desktop" or "on mobile" to target a single device.</li><li><strong>Controls</strong>: Use Undo to step back or Reset to start fresh.</li><li><strong>Note</strong>: Your chat persists on this page. Refreshing or navigating away will clear your progress.</li></ul><a href="https://docs.recurly.com/recurly-engage/docs/ai-css-assistant" target="_blank" rel="noopener noreferrer" class="underline">Learn more about styling prompts<span class="mdi mdi-open-in-new ml-1" style="font-size:14px;vertical-align:middle;" aria-hidden="true"></span></a>`;

const props = defineProps(["model", "actions"]);

const route = useRoute();
const messages = ref([{ role: "assistant", content: INTRO_HTML, isIntro: true }]);
const input = ref("");
const loading = ref(false);
const error = ref(null);
const messageList = ref(null);
const revertDialogRef = ref(null);

// Snapshot of the AI-owned CSS when this editor session began. `Revert All` returns
// to this. Captured once at setup; the component is keyed on the prompt id, so
// switching prompts (or refreshing) starts a fresh session with a fresh baseline.
const initialAiCss = ref(splitCss().aiCss);

// Index of the most recent assistant message that can be undone. Only one undo
// arrow is shown at a time and out of order undo would silently corrupt CSS state.
const lastUndoableIndex = computed(() => {
  for (let i = messages.value.length - 1; i >= 0; i--) {
    const position = messages.value[i];
    if (
      position.role === "assistant" &&
      !position.isRevertNotice &&
      position.cssSnapshot !== undefined &&
      !position.undone
    ) {
      return i;
    }
  }
  return -1;
});

const hasAiChanges = computed(() => splitCss().aiCss !== initialAiCss.value);

function splitCss() {
  const current = (props.actions.rf_settings_custom_css || "").trimEnd();
  const markerIndex = current.indexOf(AI_SECTION_MARKER);
  if (markerIndex >= 0) {
    return {
      manualCss: current.slice(0, markerIndex).trimEnd(),
      aiCss: current.slice(markerIndex + AI_SECTION_MARKER.length).trimStart(),
    };
  }
  return { manualCss: current, aiCss: "" };
}

function applyAiCss(newCss) {
  const { manualCss } = splitCss();
  const trimmed = (newCss || "").trim();
  // An empty AI section means no marker at all — leave only the manual CSS.
  if (!trimmed) {
    props.actions.rf_settings_custom_css = manualCss;
    return;
  }
  const aiSection = `${AI_SECTION_MARKER}\n${trimmed}`;
  props.actions.rf_settings_custom_css = manualCss ? `${manualCss}\n\n${aiSection}` : aiSection;
}

function revertAll() {
  if (loading.value) return;
  // Set both messages before applyAiCss — mutating the prop can trigger parent
  // reactivity in the same tick, which would wipe a subsequent push().
  messages.value = [
    { role: "assistant", content: INTRO_HTML, isIntro: true },
    {
      role: "assistant",
      content: "All changes have been reverted — the design is back to its original state.",
      isRevertNotice: true,
    },
  ];
  input.value = "";
  error.value = null;
  applyAiCss(initialAiCss.value);
}

async function send() {
  const msg = input.value.trim();
  if (!msg || loading.value) return;

  input.value = "";
  error.value = null;
  messages.value.push({ role: "user", content: msg });
  loading.value = true;

  await nextTick();
  scrollToBottom();

  try {
    const conversationHistory = messages.value
      .slice(0, -1)
      .filter(m => !m.isIntro && !m.isRevertNotice && !m.undone)
      .map(({ role, content }) => ({ role, content }));

    const { manualCss, aiCss } = splitCss();
    const result = await ApiPaths.aiDesignChat(route.params.aid, props.model.id, {
      message: msg,
      currentCss: manualCss,
      currentAiCss: aiCss,
      pathType: props.model.path_type,
      conversationHistory,
    });

    const cssSnapshot = aiCss;
    if (result.css && result.css.trim() !== aiCss.trim()) {
      applyAiCss(result.css);
    }

    messages.value.push({
      role: "assistant",
      content: result.message || "Done! The CSS has been updated.",
      cssSnapshot,
      undone: false,
    });
  } catch (err) {
    console.debug("[RfAiDesignChat] send failed:", err);
    error.value = "Something went wrong. Please try again.";
  } finally {
    loading.value = false;
    await nextTick();
    scrollToBottom();
  }
}

function undoMessage(msg) {
  if (msg.undone || loading.value) return;
  // Mark done first (sync guard), push confirmation before applyAiCss as parent
  // reactivity triggered by the prop mutation can flush in the same tick and
  // would wipe a push() that comes after.
  msg.undone = true;
  const messageIndex = messages.value.indexOf(msg);
  const userMessage = messages.value[messageIndex - 1];
  if (userMessage?.role === "user") userMessage.undone = true;
  messages.value.push({
    role: "assistant",
    content: "Done. I've reverted that change.",
    isRevertNotice: true,
  });
  applyAiCss(msg.cssSnapshot);
  nextTick().then(scrollToBottom);
}

function scrollToBottom() {
  if (messageList.value) {
    messageList.value.scrollTop = messageList.value.scrollHeight;
  }
}
</script>

<style scoped>
:deep(ul) {
  list-style-type: disc;
  padding-left: 1.25rem !important;
}
</style>
